import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableSortLabel,
    Checkbox,
    styled,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'packages/eid-ui'
import { Icon } from 'packages/eid-icons'
import * as DynamicComponents from 'components/DynamicComponents'
import { isNilOrEmpty } from 'packages/core'
import { TextFormatter, styles, renderIcon } from './utils'

const StyledTable = styled(Table)(({ theme }) => styles.table)

const StyledTableRow = styled(TableRow)(({ theme }) => styles.tableRow)

const StyledTableCell = styled(TableCell)(({ theme }) => styles.tableCell)

const StyledCheckbox = styled(Checkbox)(({ theme }) => styles.rowCheckbox)

const PeopleTable = ({
    route,
    headings,
    data,
    sort,
    sortBy,
    sortOrder,
    fallbackMessage,
    rowSelection,
    onSelect,
    selected,
    primaryKey,
}) => {
    const { t } = useTranslation()

    const getHeadingName = (heading) =>
        heading.columnName ? heading.columnName : heading.name

    const handleSort = (heading) => {
        sort(
            getHeadingName(heading),
            sortBy !== getHeadingName(heading)
                ? 'desc'
                : !sortOrder || sortOrder === '' || sortOrder === 'asc'
                ? 'desc'
                : 'asc',
        )
    }

    useEffect(() => {
        onSelect([])
    }, [data])

    const renderComponent = (a, item) => {
        const DynamicComponentToRender = DynamicComponents[a.component.name]

        return <DynamicComponentToRender attribute={a} data={item} />
    }

    const renderTableContentCell = (a, row) => {
        const cellProps = {
            align: a.align,
            style: a.styles,
        }

        return (
            <React.Fragment key={a.name}>
                {!a.hideValue &&
                    (isNilOrEmpty(row[a.name]) ? (
                        <StyledTableCell {...cellProps} style={null}>
                            -
                        </StyledTableCell>
                    ) : (
                        <StyledTableCell {...cellProps}>
                            {a.link ? (
                                <DynamicComponents.CellLink
                                    attribute={a}
                                    data={row}
                                    value={
                                        <TextFormatter
                                            item={row}
                                            attribute={a}
                                        />
                                    }
                                />
                            ) : (
                                <TextFormatter item={row} attribute={a} />
                            )}
                        </StyledTableCell>
                    ))}

                {a.avatar && (
                    <StyledTableCell {...cellProps}>
                        <DynamicComponents.CellAvatar
                            data={row}
                            attribute={a}
                        />
                    </StyledTableCell>
                )}
                {a.component && (
                    <StyledTableCell {...cellProps}>
                        {renderComponent(a, row, route)}
                    </StyledTableCell>
                )}
            </React.Fragment>
        )
    }

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = data.map((n) =>
                primaryKey ? n[primaryKey] : n.id,
            )
            onSelect(newSelecteds)
            return
        }
        onSelect([])
    }

    const handleClick = (item) => {
        const selectedIndex = selected.indexOf(
            primaryKey ? item[primaryKey] : item.id,
        )
        let newSelected = []

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(
                selected,
                primaryKey ? item[primaryKey] : item.id,
            )
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1))
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1))
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            )
        }

        onSelect(newSelected)
    }

    const isSelected = (id) => selected.indexOf(id) !== -1

    return (
        <StyledTable style={{ height: 'fit-content' }}>
            <TableHead variant="eidHead">
                <StyledTableRow>
                    {rowSelection && (
                        <TableCell
                            padding="checkbox"
                            scope="col"
                            sx={{
                                paddingLeft: '0.8rem',
                                paddingBlock: '1.2rem',
                            }}
                        >
                            <Checkbox
                                icon={<Icon name="CheckBox" />}
                                checkedIcon={<Icon name="CheckedBox" />}
                                checked={
                                    data?.length > 0 &&
                                    selected.length === data?.length
                                }
                                onChange={handleSelectAllClick}
                                data-ai-explain="Select all delegations"
                            />
                        </TableCell>
                    )}

                    {headings.map((heading) => (
                        <TableCell
                            data-protectedsubcomponent={
                                heading?.requireAccess?.control
                                    ? heading.requireAccess.control
                                    : 'NotProtected'
                            }
                            key={heading.name}
                            align={heading.align}
                            scope="col"
                            data-ai-explain={`Column: ${t(heading.label)}`}
                            sx={{
                                paddingLeft: '0.8rem',
                                paddingBlock: '1.2rem',
                            }}
                        >
                            <>
                                {heading.sort ? (
                                    <Tooltip
                                        title={
                                            sortBy ===
                                                getHeadingName(heading) &&
                                            sortOrder === 'desc'
                                                ? t('SortAscending')
                                                : t('SortDescending')
                                        }
                                    >
                                        <TableSortLabel
                                            active={
                                                sortBy ===
                                                getHeadingName(heading)
                                            }
                                            direction={
                                                sortBy ===
                                                getHeadingName(heading)
                                                    ? sortOrder
                                                    : 'asc'
                                            }
                                            onClick={() => handleSort(heading)}
                                            data-ai-explain={`Sort by ${t(heading.label)}`}
                                        >
                                            {t(heading.label)}
                                        </TableSortLabel>
                                    </Tooltip>
                                ) : (
                                    <span style={styles.tableHeaderContent}>
                                        {t(heading.label)}
                                        {renderIcon(
                                            heading,
                                            sort,
                                            sortBy,
                                            sortOrder,
                                        )}
                                    </span>
                                )}
                            </>
                        </TableCell>
                    ))}
                </StyledTableRow>
            </TableHead>

            <TableBody>
                {data.length === 0 && (
                    <StyledTableRow>
                        <StyledTableCell
                            colSpan={
                                headings.length + (rowSelection ? 1 : 0)
                            }
                            style={{ textAlign: 'center' }}
                            data-ai-explain="Empty delegations table"
                        >
                            {fallbackMessage}
                        </StyledTableCell>
                    </StyledTableRow>
                )}

                {data.map((row) => (
                    <StyledTableRow
                        key={row.id}
                        data-ai-explain={`Delegation row: ${
                            row.delegateeFriendlyName ||
                            row.delegatorFriendlyName ||
                            'entry'
                        }`}
                    >
                        {rowSelection && (
                            <StyledTableCell padding="checkbox">
                                <StyledCheckbox
                                    icon={<Icon name="CheckBox" />}
                                    checkedIcon={<Icon name="CheckedBox" />}
                                    checked={isSelected(
                                        primaryKey ? row[primaryKey] : row.id,
                                    )}
                                    onChange={() => handleClick(row)}
                                    data-ai-explain={`Select delegation row: ${
                                        row.delegateeFriendlyName ||
                                        row.delegatorFriendlyName ||
                                        'entry'
                                    }`}
                                />
                            </StyledTableCell>
                        )}
                        {headings.map((a) => renderTableContentCell(a, row))}
                    </StyledTableRow>
                ))}
            </TableBody>
        </StyledTable>
    )
}

PeopleTable.propTypes = {
    headings: PropTypes.array.isRequired,
    data: PropTypes.array.isRequired,
}

export default PeopleTable
